import { SearchContainer, Container, FilterContainer } from "./index.styles";
import UI from "@mile-meeting/mile-ui";
import TableHeader from "./table-header";
import MemberItem from "./member-item";
import { useEffect, useState } from "react";
import { deleteMember, getMemberList, TMember, updateMemberRole } from "apis/members";
import { useRecoilState } from "recoil";
import { user, workspace } from "recoil/recoilState";
import { debounce } from "lodash";
import MemberDeleteModal from "./member-delete-modal";
import { useToast } from "hook/useToast";
import { roleToString } from "utils/role";
import { getMemberIntegration } from "apis/member-integration";

const AllMembers = ({ open }: { open: boolean }) => {
  const { showToast } = useToast();
  const [userInfo, setUserInfo] = useRecoilState(user);
  const [workspaceInfo, setWorkspacesInfo] = useRecoilState(workspace);

  const [totalPage, setTotalPage] = useState<number>(1);
  const [page, setPage] = useState<number>(1);
  const [members, setMembers] = useState<TMember[]>([]);
  const [searchRole, setSearchRole] = useState<number>();
  const [searchMember, setSearchMember] = useState<string>();
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [clickItem, setClickItem] = useState<TMember>();
  const [gwsDomainList, setGwsDomainList] = useState<string[]>();

  const onHandleDeleteFilter = () => {
    setSearchRole(undefined);
    getMemberListByFilter(1, undefined, searchMember);
  };

  const onHandleMemberDelete = () => {
    setOpenDeleteModal(true);
  };

  const onChangeRole = async (id: string, role: number) => {
    const res = await updateMemberRole(id, role);
    if (res.success) {
      showToast("멤버 권한이 성공적으로 변경되었습니다.", "success");
      getMemberListByFilter(page, searchRole, searchMember);
    } else {
      showToast(res.message ?? "멤버 권한 설정이 실패되었습니다. 잠시 후 다시 시도해주세요.", "error");
    }
  };

  const onDeleteMember = async (id: string) => {
    const res = await deleteMember(id);
    if (res.success) {
      showToast("멤버 삭제가 완료되었습니다.", "success");
      getMemberListByFilter(page, searchRole, searchMember);
    } else {
      showToast("멤버 삭제가 실패되었습니다. 잠시 후 다시 시도해주세요.", "error");
    }
    setOpenDeleteModal(false);
  };

  const getMemberListByFilter = async (page: number, searchRole?: number, searchMember?: string) => {
    const res = await getMemberList(page, searchRole, searchMember);
    if (res) {
      setTotalPage(res.totalPageNumber ?? 1);
      setMembers(res.memberList ?? []);
      setPage(res?.nowPageNumber ?? 1);
    }
  };

  const getGwsDomainList = async () => {
    const res = await getMemberIntegration();
    if (res.success) {
      setGwsDomainList(res.data?.domains);
    }
  };

  useEffect(() => {
    if (open) {
      getMemberListByFilter(1, searchRole, searchMember);
      getGwsDomainList();
    }
  }, [open]);

  return (
    <Container style={{ display: open ? "flex" : "none" }}>
      <SearchContainer>
        <FilterContainer>
          {searchRole !== undefined && (
            <UI.Chip.Contained
              size="medium"
              rightIcon="x"
              title={`권한: ${roleToString(searchRole)}`}
              showRightIcon
              onClick={onHandleDeleteFilter}
            />
          )}
        </FilterContainer>
        <UI.Input
          placeholder="멤버를 검색해 주세요."
          onChange={debounce((value) => {
            getMemberListByFilter(1, searchRole, value);
            setSearchMember(value);
          }, 500)}
          prefixIcon="search"
          showPrefixIcon
          width={280}
        />
      </SearchContainer>
      <TableHeader
        selectedRole={searchRole}
        onChangeRole={(value: number) => {
          setSearchRole(value);
          getMemberListByFilter(1, value, searchMember);
        }}
      />
      {members.map((item, index) => (
        <MemberItem
          key={item.id}
          name={item.nickname}
          email={item.email}
          image={item.image}
          gwsOrgUnit={item.googleOrgUnit}
          gwsDomainList={gwsDomainList}
          onChangeRole={(role) => {
            onChangeRole(item.id, role);
          }}
          onHandleDelete={() => {
            setClickItem(item);
            onHandleMemberDelete();
          }}
          isMe={userInfo?.email === item.email}
          role={item.role}
        />
      ))}
      <div style={{ marginTop: "48px" }}>
        {totalPage !== 0 && (
          <UI.Pagination
            pageCount={totalPage}
            currentPage={page}
            onChange={(page: number) => {
              if (page === 0) return;
              getMemberListByFilter(page, searchRole, searchMember);
            }}
          />
        )}
      </div>
      <MemberDeleteModal
        title={`멤버 "${clickItem?.nickname ?? "-"}"를 삭제하시겠습니까?`}
        content={
          <p style={{ textAlign: "center" }}>
            해당 멤버가 더이상 {workspaceInfo?.name ?? "-"}에
            <br />
            접속할 수 없게 됩니다.
          </p>
        }
        modalIsOpen={openDeleteModal}
        closeModal={() => setOpenDeleteModal(false)}
        onChange={() => onDeleteMember(clickItem?.id ?? "")}
      />
    </Container>
  );
};

export default AllMembers;
